import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box, Button, Divider, Snackbar, Typography,
} from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import AvatarIndicator from '../../../../../../../../../../Common/AvatorIndicator';
import { SessionSummaryFieldsFragment } from '../../../../../../../../../../generated/core-api/graphql-react-query';
import useTimezone from '../../../../../../../../../../utils/useTimezone';
import { useUserInfoData } from '../../../../../../../../../Hooks/useUserInfo';
import withSessionDetailsSectionErrorBoundary from '../SessionDetailsSectionErrorBoundary';
import useListRelatedSessions from '../SessionEvaluationSheets/Hooks/useListRelatedSessions';
import StyledBox from './styles';

interface SessionInfoProps {
  session: SessionSummaryFieldsFragment;
}
function SessionInfo(props: SessionInfoProps) {
  const { session } = props;
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('session');
  const dateFormat = new Intl.DateTimeFormat('en-US', { dateStyle: 'short' });
  const timeFormat = new Intl.DateTimeFormat('en-US', { timeStyle: 'short' });
  const userInfo = useUserInfoData();
  const [copiedKey, setCopiedKey] = React.useState<'callId' | 'callGroupId' | null>(null);
  const data = useListRelatedSessions(session, 'externalCallGroupId');
  const { externalCallId, externalCallGroupId } = session;
  const timezone = useTimezone();

  return (
    <>
      <StyledBox>
        <Box>
          <Typography variant="subtitle2">
            {tCommon('createdBy')}
          </Typography>
          <Box className="user">
            <AvatarIndicator user={session.userField} tooltip />
            <Typography>
              {session.userField.displayName}
            </Typography>
            {session.userField.displayName === userInfo.id && (
              <Typography>
                {`(${tCommon('you')})`}
              </Typography>
            )}
          </Box>
        </Box>
        <Box>
          <Typography variant="subtitle2">
            {tCommon('date')}
          </Typography>
          <Typography>
            {dateFormat.format(session.date)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2">
            {tCommon('time')}
          </Typography>
          <Box className="timezone">
            <Typography>
              {timeFormat.format(session.date)}
            </Typography>
            <Typography variant="caption">
              {`(${timezone})`}
            </Typography>
          </Box>
        </Box>
        {externalCallId && (
          <Box className="external-call-box">
            <Box>
              <Typography variant="subtitle2">
                {t('details.callId')}
              </Typography>
              <Box className="copy-box">
                <Button onClick={() => {
                  navigator.clipboard.writeText(externalCallId.split('_').slice(2).join('_'));
                  setCopiedKey('callId');
                  setTimeout(() => setCopiedKey(null), 2000);
                }}
                >
                  {copiedKey === 'callId' ? <CheckIcon /> : <ContentCopyIcon />}
                </Button>
              </Box>
            </Box>
            <Typography variant="body1" title={externalCallId.split('_').slice(2).join('_')}>
              {externalCallId.split('_').slice(2).join('_')}
            </Typography>
          </Box>
        )}
        {externalCallGroupId && (
          <>
            <Box className="external-call-box">
              <Box>
                <Typography variant="subtitle2">
                  {t('details.callGroupId')}
                </Typography>
                <Box className={clsx('copy-box', 'group-id')}>
                  <Button onClick={() => {
                    navigator.clipboard.writeText(externalCallGroupId);
                    setCopiedKey('callGroupId');
                    setTimeout(() => setCopiedKey(null), 2000);
                  }}
                  >
                    {copiedKey === 'callGroupId' ? <CheckIcon /> : <ContentCopyIcon />}
                  </Button>
                </Box>
              </Box>
              <Typography variant="body1" title={externalCallGroupId}>
                {externalCallGroupId}
              </Typography>
            </Box>
            {data?.sessions && (
              <Box>
                <Typography variant="subtitle2">
                  {t('details.total-group-calls')}
                </Typography>
                <Typography>
                  {data.sessions.length + 1}
                </Typography>
              </Box>
            )}
          </>
        )}
        <Snackbar open={!!copiedKey} autoHideDuration={3000} onClose={() => setCopiedKey(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="success">
            {t(`details.copy-success.${copiedKey}`)}
          </Alert>
        </Snackbar>
      </StyledBox>
      <Divider />
    </>
  );
}

export default withSessionDetailsSectionErrorBoundary(SessionInfo, 'session info');
